// Authentication and authorization types

/**
 * Permission levels in hierarchical order
 * - read: Can view orders, positions, account details
 * - order_placement: Can submit/cancel/replace orders (includes Read)
 * - management: Can manage API keys and grant permissions (includes OrderPlacement)
 */
export type Permission = 'read' | 'order_placement' | 'management';

/**
 * User information
 */
export interface User {
  id: string;
  email: string;
  is_superadmin: boolean;
  approved: boolean;
}

/**
 * Account with permission level
 */
export interface Account {
  id: string;              // UUID
  account_id: string;      // String identifier (e.g., "USER-ABC123")
  permission: Permission;
}

/**
 * Account summary in login/profile responses
 */
export interface AccountSummary {
  id: string;              // UUID
  account_id: string;      // String identifier
  permission: string;      // Permission as string
}

/**
 * Login request payload
 */
export interface LoginRequest {
  email: string;
  password: string;
  account_id?: string;     // Optional account UUID to scope JWT
}

/**
 * Login response with JWT and account info
 */
export interface LoginResponse {
  user_id: string;
  email: string;
  is_superadmin: boolean;
  approved: boolean;
  token: string;
  refresh_token: string;
  account_id: string;      // Selected account UUID (empty for unscoped superadmin)
  permission: string;      // Permission on selected account (empty for unscoped superadmin)
  expires_at: string;      // ISO 8601 timestamp
  refresh_expires_at: string; // ISO 8601 timestamp
  accounts: AccountSummary[];  // All accessible accounts
}

/**
 * Signup request payload
 */
export interface SignupRequest {
  email: string;
  password: string;
}

/**
 * Signup response with JWT and default account
 */
export interface SignupResponse {
  user_id: string;
  email: string;
  account_id: string;      // Default account UUID
  account_identifier: string; // Account string identifier (e.g. "USER-12345678")
  approved: boolean;        // Always false on signup (pending admin approval)
  token: string;
  refresh_token: string;
  expires_at: string;      // ISO 8601 timestamp
  refresh_expires_at: string; // ISO 8601 timestamp
}

/**
 * Refresh token request payload
 */
export interface RefreshTokenRequest {
  refresh_token: string;
}

/**
 * Refresh token response
 */
export interface RefreshTokenResponse {
  token: string;
  refresh_token: string;
  expires_at: string;
  refresh_expires_at: string;
  account_id: string;      // Empty for unscoped superadmin
  permission: string;      // Current permission from DB
  is_superadmin: boolean;
  approved: boolean;
}

/**
 * Logout request payload
 */
export interface LogoutRequest {
  refresh_token: string;
}

/**
 * Helper to check if a permission includes another
 * Management > OrderPlacement > Read
 */
export function hasPermission(userPermission: Permission, required: Permission): boolean {
  const hierarchy: Record<Permission, number> = {
    read: 1,
    order_placement: 2,
    management: 3,
  };

  return hierarchy[userPermission] >= hierarchy[required];
}

/**
 * Parse permission string to Permission type
 */
export function parsePermission(permission: string): Permission {
  const normalized = permission.toLowerCase();
  if (normalized === 'read' || normalized === 'order_placement' || normalized === 'management') {
    return normalized as Permission;
  }
  throw new Error(`Invalid permission: ${permission}`);
}
